import React, { useState } from 'react';
import { getStrategyConfig, RISK_COLORS } from '../config/strategyConfig';

/**
 * StrategyComparison Component
 * Side-by-side comparison table of all analyzed strategies
 */
function StrategyComparison({ strategies, recommendedStrategy, onSelectStrategy }) {
  const [expanded, setExpanded] = useState(false);

  if (!strategies || strategies.length === 0) return null;

  // Remove duplicates by strategy key (keep first occurrence)
  const uniqueStrategies = strategies.filter((s, index, self) => 
    index === self.findIndex((t) => t.strategy === s.strategy)
  );

  // Sort by score descending
  const sortedStrategies = [...uniqueStrategies].sort((a, b) => b.score - a.score);

  const getRiskDisplay = (riskLevel) => {
    const risk = RISK_COLORS[riskLevel] || RISK_COLORS.moderate;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${risk.bg} ${risk.text}`}>
        {risk.label}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">📊</span>
          <span className="font-bold text-gray-900">Strategy Comparison</span>
          <span className="text-sm text-gray-500">
            ({uniqueStrategies.length} strategies analyzed)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {expanded ? 'Collapse' : 'Expand'}
          </span>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-sm font-semibold text-gray-700">Strategy</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Score</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Est. Profit</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Cash-on-cash (Y1)</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Timeline</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Cash to complete</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Risk</th>
                  <th className="text-center py-3 px-2 text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedStrategies.map((strategy, index) => {
                  const config = getStrategyConfig(strategy.strategy);
                  const isRecommended = strategy.recommended;
                  const isTop3 = index < 3;

                  return (
                    <tr 
                      key={strategy.strategy}
                      className={`border-b border-gray-100 ${isRecommended ? 'bg-green-50/50' : ''} hover:bg-gray-50`}
                    >
                      <td className="py-4 px-2">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{config.icon}</span>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {config.displayName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {config.shortName}
                            </div>
                          </div>
                          {isRecommended && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
                              ★
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-center py-4 px-2">
                        <div className={`text-xl font-bold ${isRecommended ? 'text-green-700' : 'text-gray-900'}`}>
                          {strategy.score != null ? strategy.score : '—'}
                        </div>
                        <div className="text-xs text-gray-500">/ 100</div>
                      </td>
                      <td className="text-center py-4 px-2">
                        <div className="font-semibold text-gray-900">
                          {strategy.estimated_profit != null ? `£${Number(strategy.estimated_profit).toLocaleString()}` : '—'}
                        </div>
                      </td>
                      <td className="text-center py-4 px-2">
                        <div className={`font-semibold ${isTop3 ? 'text-green-700' : 'text-gray-900'}`}>
                          {strategy.estimated_roi_percent != null ? `${Number(strategy.estimated_roi_percent).toFixed(1)}%` : '—'}
                        </div>
                      </td>
                      <td className="text-center py-4 px-2">
                        <div className="font-semibold text-gray-900">
                          {strategy.timeline_months != null ? `${strategy.timeline_months} mo` : '—'}
                        </div>
                      </td>
                      <td className="text-center py-4 px-2">
                        <div className="font-semibold text-gray-900">
                          {strategy.initial_capital_required != null ? `£${Number(strategy.initial_capital_required).toLocaleString()}` : '—'}
                        </div>
                      </td>
                      <td className="text-center py-4 px-2">
                        {strategy.risk_level ? getRiskDisplay(strategy.risk_level) : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="text-center py-4 px-2">
                        <button
                          onClick={() => onSelectStrategy?.(strategy.strategy)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            isRecommended
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {isRecommended ? 'View' : 'Compare'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-50 border border-green-200 rounded"></span>
              <span>Recommended Strategy</span>
            </div>
            <div className="flex items-center gap-2">
              <span>★</span>
              <span>Top Recommendation</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StrategyComparison;
